import { useFormik } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { useMutation } from 'react-query';

import { Button, Form, InputField, Loader } from '@/components';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import {
  requestChangeName,
  requestCheckNameAvailability,
} from '@/services/friendsCup';
import { FCupViewTeamResponse, RequestChangeNameFCup } from '@/types/friendCup';
import {
  nameFCupSchema,
  oneSpaceBetweenWordMinTwoChar,
  TEXTS,
  validateFieldError,
} from '@/utils';

export const FormInfoTourney = React.memo(
  ({ onSubmit }: { onSubmit?: () => void }) => {
    const toast = useToast();
    const { tourneyData } = useTourney();

    const team: FCupViewTeamResponse = useMemo(() => {
      return tourneyData.tourney;
    }, [tourneyData]);

    // post cambio nombre
    const { isLoading: isLoadingNewName, mutateAsync: onRequestChangeName } =
      useMutation(
        async (data: RequestChangeNameFCup) => await requestChangeName(data),
        {
          onSuccess: (data) => {
            if (data.estado) {
              toast.show('Cambio realizado exitosamente');
              setStatus(false);
              onSubmit && onSubmit();
            } else {
              toast.show(data.mensaje);
            }
          },
          onError: (err) => {
            const error = err as Error;
            toast.show(error.message);
          },
        }
      );
    useEffect(() => {
      setFieldValue('teamName', team.nombreGrupo);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [team.nombreGrupo]);

    const {
      touched,
      errors,
      isSubmitting,
      setFieldValue,
      setFieldError,
      handleSubmit,
      setFieldTouched,
      values,
      handleBlur,
      setStatus,
      status,
    } = useFormik({
      initialValues: {
        teamName: '',
      },
      validateOnMount: false,
      validateOnBlur: true,
      validationSchema: nameFCupSchema,
      onSubmit: async (val: { teamName: string }, { setSubmitting }) => {
        if (team.nombreGrupo !== val.teamName) {
          onRequestChangeName({
            idGrupo: tourneyData.id,
            nombre: val.teamName,
          });
          setSubmitting(false);
        }
      },
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = oneSpaceBetweenWordMinTwoChar(e.target.value);

      setFieldValue('teamName', value);
      setFieldTouched('teamName', true, false);

      if (value.trim().length === 0) {
        setFieldError('teamName', TEXTS.minLetters);
        setStatus(false);
      } else if (value.trim() !== team.nombreGrupo) {
        handleNameTournamentValidation(value);
      }
    };

    const MIN_CHARACTERS_TO_VALIDATE_NAME = 2;
    const handleNameTournamentValidation = async (teamName: string) => {
      if (teamName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME) {
        const response = await requestCheckNameAvailability(
          encodeURIComponent(teamName)
        );
        if (!response.estado && 'codError' in response) {
          if (response.codError === 1603) {
            setFieldError('teamName', TEXTS.nameExist);
          } else {
            setFieldError('teamName', response.mensaje);
          }
          setStatus(false);
        } else {
          setFieldError('teamName', undefined);
          setStatus(true);
        }
      } else {
        setStatus(false);
      }
    };

    const isSubmitable = useMemo(() => {
      return (
        status &&
        !errors.teamName &&
        values.teamName !== team.nombreGrupo &&
        values.teamName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME &&
        !isSubmitting
      );
    }, [
      status,
      errors.teamName,
      values.teamName,
      team.nombreGrupo,
      isSubmitting,
    ]);

    return (
      <>
        <Loader isVisible={isLoadingNewName} />
        <Form.Form
          className="px-4 lg:px-0 mt-4"
          loading={isSubmitting}
          onSubmit={handleSubmit}
        >
          <Form.Field>
            <InputField
              behavior="default"
              error={validateFieldError(
                touched.teamName,
                errors.teamName,
                values.teamName?.length
              )}
              label="Nombre del equipo"
              maxLength={25}
              name="teamName"
              placeholder="Nombre del equipo"
              success={
                !errors.teamName &&
                status &&
                values.teamName !== team.nombreGrupo
              }
              value={values.teamName}
              onBlur={(e) => {
                if (e.target.value.length >= 2) return;
                handleBlur(e);
              }}
              onChange={handleInputChange}
            />
          </Form.Field>
          <div className="lg:mt-9 mt-6">
            <Button
              className="w-full lg:max-w-[215px]"
              disabled={!isSubmitable}
              type="button"
              variant="primary"
              onClick={handleSubmit}
            >
              Guardar cambios
            </Button>
          </div>
        </Form.Form>
      </>
    );
  }
);

FormInfoTourney.displayName = 'FormInfoTourney';
